import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { formatErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { ArrowRight } from "lucide-react";
import Logo from "../components/Logo";
import { GoogleLogin } from "@react-oauth/google";
import api from "../lib/api";

export default function Register() {
  const { register } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await register(email, password, name);
      navigate("/dashboard");
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setErr(""); setLoading(true);
    try {
      await api.post('/auth/google', { token: credentialResponse.credential });
      navigate("/dashboard");
      window.location.reload(); // Ensure auth context picks up the new cookie
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Google Registration failed");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] grid lg:grid-cols-2" data-testid="register-page">
      <div className="hidden lg:block relative bg-[color:var(--brand-ink)] text-white">
        <div className="absolute inset-0 grid-bg opacity-10" />
        <div className="aurora opacity-60" />
        <div className="relative h-full flex flex-col p-12 justify-between">
          <Link to="/" className="font-display text-2xl">Before<span className="text-[color:var(--brand-blue)]">Exam</span></Link>
          <div>
            <h2 className="font-display text-4xl tracking-tighter leading-tight max-w-md">
              Build a streak. Crush certifications. Compound your career.
            </h2>
            <ul className="mt-6 space-y-2 text-white/70 text-sm">
              <li>✓ Free access to sample mocks</li>
              <li>✓ 184,000+ engineers practicing</li>
              <li>✓ 30-day money-back guarantee</li>
            </ul>
          </div>
          <div className="text-sm text-white/50">© BeforeExam Labs</div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <h1 className="font-display text-3xl tracking-tight">Create your free account</h1>
          <p className="text-sm text-muted-foreground mt-1">Try sample tests free. No credit card required.</p>

          <div className="mt-6 w-full flex justify-center" data-testid="google-signup-button">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={() => setErr('Google Signup Failed')}
              theme="outline"
              size="large"
              width="100%"
              text="signup_with"
            />
          </div>

          <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
            <div className="h-px bg-border flex-1" /> or with email <div className="h-px bg-border flex-1" />
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="register-form">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} required
                className="mt-1 h-11 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-foreground"
                data-testid="register-name" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1 h-11 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-foreground"
                data-testid="register-email" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Password</label>
              <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1 h-11 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-foreground"
                data-testid="register-password" />
            </div>
            {err && <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive" data-testid="register-error">{err}</div>}
            <Button disabled={loading} type="submit" className="h-11 w-full btn-primary" data-testid="register-submit">
              {loading ? "Creating account…" : <>Create account <ArrowRight className="ml-2 h-4 w-4" /></>}
            </Button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="font-semibold text-foreground hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
